import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Button } from "@mui/material";
import "../App.css";
import "aos/dist/aos.css";
import AOS from "aos";

const Help = () => {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 1000 });
    AOS.refresh();
  }, []);

  return (
    <div
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        fontFamily: "'Baloo Bhaijaan 2', cursive",
        color: "white",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Header */}
      <header
        style={{
          width: "100%",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.5)",
        }}
      >
        <img
          src={require("../assets/images/logo_name.PNG")}
          alt="YourStoryWorld Logo"
          style={{
            height: "40px",
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        />
        <div>
          <Button color="inherit" onClick={() => navigate("/")}>
            Home
          </Button>
          <Button color="inherit" onClick={() => navigate("/login")}>
            Sign In
          </Button>
          <Button color="inherit" onClick={() => navigate("/subscriptions")}>
            Subscriptions
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          padding: "40px 24px",
          borderRadius: "10px",
          maxWidth: "900px",
          margin: "20px auto",
          color: "#333",
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ color: "#007BFF", fontWeight: "bold" }}>
          📚 Help & FAQs
        </Typography>
        <Typography variant="body1" paragraph>
          Need assistance? We’ve got you covered. Browse the sections below to find answers to the most
          common questions about YourStoryWorld.
        </Typography>

        {/* Account Management */}
        <section style={{ marginTop: "30px" }} data-aos="fade-up">
          <Typography variant="h5" gutterBottom>
            🔐 Account Management
          </Typography>
          <ul style={{ paddingLeft: "18px" }}>
            <li>
              <strong>How do I create a story?</strong> – Sign in, go to your dashboard, select your
              profile, and click <em>“Create a Story”</em>.
            </li>
            <li>
              <strong>I forgot my password.</strong> – On the sign-in page, click{" "}
              <em>“Forgot Password”</em> and follow the instructions in the email you receive.
            </li>
            <li>
              <strong>How many family profiles can I add?</strong> – The Family Plan supports up to 4
              profiles, each with their own reading history.
            </li>
            <li>
              <strong>Who resets student passwords?</strong> – For school accounts, passwords are
              managed by the teacher or school administrator.
            </li>
          </ul>
        </section>

        {/* Story Features */}
        <section style={{ marginTop: "30px" }} data-aos="fade-up">
          <Typography variant="h5" gutterBottom>
            📖 Story Features
          </Typography>
          <ul style={{ paddingLeft: "18px" }}>
            <li>
              <strong>What is Diversity Mode?</strong> – When enabled, stories include characters,
              names, and settings from a wide range of backgrounds so children can see themselves in
              what they read.
            </li>
            <li>
              <strong>How does phonics mode work?</strong> – Phonics-enhanced stories simplify words
              and highlight sounds to support early readers, especially in the younger age groups.
            </li>
            <li>
              <strong>How do I listen to a story?</strong> – After a story is generated, use the{" "}
              <em>speaker</em> or <em>play</em> button to hear it read aloud.
            </li>
            <li>
              <strong>Can I save or download stories?</strong> – Stories stay in your library so you
              can revisit them. Some plans also allow printing or saving as a PDF (where available).
            </li>
          </ul>
        </section>

        {/* Dashboards & Insights */}
        <section style={{ marginTop: "30px" }} data-aos="fade-up">
          <Typography variant="h5" gutterBottom>
            📊 Dashboards & Insights
          </Typography>
          <ul style={{ paddingLeft: "18px" }}>
            <li>
              <strong>What are reading streaks?</strong> – A streak is the number of days in a row
              where at least one story was read or listened to.
            </li>
            <li>
              <strong>Where do I see comprehension scores?</strong> – If comprehension questions are
              enabled for your account, scores appear in the <em>“Reading Insights”</em> or
              dashboard graphs.
            </li>
            <li>
              <strong>What can schools see?</strong> – Teachers and schools see anonymised class
              trends and per-student reading activity, such as stories read, time spent, genres
              explored and quiz results.
            </li>
          </ul>
        </section>

        {/* Schools & Teachers */}
        <section style={{ marginTop: "30px" }} data-aos="fade-up">
          <Typography variant="h5" gutterBottom>
            🏫 For Schools & Teachers
          </Typography>
          <ul style={{ paddingLeft: "18px" }}>
            <li>
              <strong>How are student accounts created?</strong> – Teachers or admins create student
              logins from the School Dashboard. Usernames and passwords can be exported and shared
              with the class.
            </li>
            <li>
              <strong>How do I assign themes?</strong> – Use the <em>Themes</em> section in your
              dashboard to assign weekly or topic-based themes for your class.
            </li>
            <li>
              <strong>Where do I find student recordings?</strong> – Student voice recordings are
              visible in the Teacher or School Dashboard, where you can listen back and give
              feedback.
            </li>
          </ul>
        </section>

        {/* Plans & Billing */}
        <section style={{ marginTop: "30px" }} data-aos="fade-up">
          <Typography variant="h5" gutterBottom>
            💳 Plans & Billing
          </Typography>
          <ul style={{ paddingLeft: "18px" }}>
            <li>
              <strong>Is there a free plan?</strong> – Yes. Our Free Plan gives you a limited number
              of stories each month with no credit card required.
            </li>
            <li>
              <strong>How do I upgrade or change plan?</strong> – Go to the{" "}
              <em>Subscriptions</em> page after signing in to view or upgrade your plan.
            </li>
            <li>
              <strong>Can schools get invoices?</strong> – Yes. School plans include invoice and
              receipt options suitable for finance teams.
            </li>
          </ul>
        </section>

        {/* Still need help */}
        <section style={{ marginTop: "30px" }} data-aos="fade-up">
          <Typography variant="h5" gutterBottom>
            📩 Still Need Help?
          </Typography>
          <Typography variant="body1" paragraph>
            If you can’t find what you’re looking for, we’re happy to help.
          </Typography>
          <Typography variant="body1">
            📧 Email:{" "}
            <a href="mailto:support@yourstoryworld.com" style={{ color: "#007BFF" }}>
              support@yourstoryworld.com
            </a>
            <br />
            📬 Or use the contact form on our{" "}
            <a href="/contact" style={{ color: "#007BFF" }}>
              Contact Us
            </a>{" "}
            page.
          </Typography>
        </section>
      </main>

      {/* Footer */}
      <footer
        style={{
          textAlign: "center",
          padding: "20px",
          backgroundColor: "#000",
          color: "#fff",
        }}
      >
        <Typography>&copy; {new Date().getFullYear()} YourStoryWorld | All Rights Reserved</Typography>
        <Typography variant="body2">
          <a href="/about" style={{ color: "white", textDecoration: "none" }}>
            About Us
          </a>{" "}
          |{" "}
          <a href="/contact" style={{ color: "white", textDecoration: "none" }}>
            Contact
          </a>{" "}
          |{" "}
          <a href="/feedback" style={{ color: "white", textDecoration: "none" }}>
            Feedback
          </a>
        </Typography>

        <Typography
          variant="caption"
          style={{ marginTop: "10px", display: "block", color: "#ccc" }}
        >
          ⚠️ Disclaimer: The stories generated by YourStoryWorld are created using
          AI and are intended for entertainment and educational purposes only.
          While we strive for accuracy, some content may contain factual or
          narrative inconsistencies.
          <p style={{ fontSize: "0.8rem", color: "#e11d48", marginTop: "10px" }}>
  <strong>Child-Safe Content:</strong> YourStoryWorld uses AI safety filters to ensure all stories 
  remain age-appropriate. Violent, explicit, discriminatory, or harmful content is automatically 
  removed or rewritten. Minor variance may occur due to the nature of AI generation.
</p>

        </Typography>
      </footer>
    </div>
  );
};

export default Help;
